import React from 'react';
import { subWeeks, addDays } from 'date-fns';
import Button from '@ingka/button';
import chevronLeftIcon from '@ingka/ssr-icon/paths/chevron-left';
import chevronRightIcon from '@ingka/ssr-icon/paths/chevron-right';
import { styled } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { mediaQueries } from '../../../styles/mediaQueries';
import { useStateDispatch } from '../../../store/';
import { updateNumberOfWeeksBack } from '../../../reducers/timeslots/timeSlotsReducer';
import { selectNumberOfWeeksBack } from '../../../reducers/actionSelectors';

interface NextPrevProps {
  firstDate: string;
  lastDate: string;
  numberOfWeeks: number;
  onNextClick?: (date: string) => void;
  onPrevClick?: (date: string) => void;
  nextEnabled: boolean;
  prevEnabled: boolean;
  loading?: boolean;
  children: React.ReactNode;
}

const StyledContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 0 16px 0;

  ${mediaQueries.landscapePhoneUp} {
    margin: 0 0 24px 0;
  }
`;

const NextPrev: React.FC<NextPrevProps> = ({
  firstDate,
  lastDate,
  numberOfWeeks,
  onNextClick,
  onPrevClick,
  nextEnabled,
  prevEnabled,
  loading,
  children,
}) => {
  const { t } = useTranslation();
  const dispatch = useStateDispatch();

  const handleNextClick = () => {
    if (onNextClick) {
      dispatch(updateNumberOfWeeksBack(numberOfWeeks));
      onNextClick(addDays(lastDate, 1).toISOString());
    }
  };

  const handlePrevClick = () => {
    const numberOfWeeksToSubtract = dispatch(selectNumberOfWeeksBack());
    const startDay = subWeeks(firstDate, numberOfWeeksToSubtract).toISOString();
    if (onPrevClick) {
      onPrevClick(startDay);
    }
  };

  return (
    <StyledContainer>
      <Button
        type="tertiary"
        data-cy="ts-picker-prev"
        onClick={handlePrevClick}
        disabled={!prevEnabled || loading}
        aria-label={t('pages.timeslots.ariaPrevText')}
        ssrIcon={chevronLeftIcon}
        iconOnly
      />
      {children}
      <Button
        type="tertiary"
        data-cy="ts-picker-next"
        onClick={handleNextClick}
        disabled={!nextEnabled || loading}
        aria-label={t('pages.timeslots.ariaNextText')}
        ssrIcon={chevronRightIcon}
        iconOnly
      />
    </StyledContainer>
  );
};

export default NextPrev;
